import Contact from '@Components/Contact';
import { usePickupDropoffAccess } from '@Hook/usePickupDropoffAccess';
import { useAiValidation } from '../../../hooks/useAiValidation';
import {
  FormHelperText,
  Grid,
  SelectChangeEvent,
  TextField,
  Typography,
  useTheme,
  Paper,
  Chip,
  Box,
  alpha,
  Card,
  CardContent,
  Collapse,
  IconButton,
  Alert,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { ValidationResult } from 'joi';
import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import SBDropdown from '../../../components/Dropdown';
import { DropdownOptionType } from '../../../components/Dropdown/Type';
import GoogleMaps from '../../../components/Location';
import SbTextField from '../../../components/Textfield';
import CreateShipmentContext from '../../../context/CreateShipment';
import { getFormattedLocation } from '../../../services/formateLocation';
import { AppDispatch, RootStateType } from '../../../store';
import { addAddress } from '../../../store/reducers/SavedAddress';
import { AddressType } from '../../../store/reducers/SavedAddress/Type';
import SavedAddresses from '../SavedAddresses';
import { createShipmentContextType, userDetailsErrorType, userDetailsType } from '../../../types/shipment';
import ShipmentPickupType from '../ShipmentPickupType';
import InsurancePackaging from '../InsurancePackaging';
import Divider from '@mui/material/Divider';
import {
  defaultErrorState,
  defaultShipperState,
  defaultConsignee,
  defaultPackageState,
  defaultShipmentTypeState,
  defaultBoxesState,
} from '../constants';
import { ShipmentUserSchema } from '../UserDetails/Schema';
import SbButton from '../../../components/Button';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { useNavigate } from 'react-router-dom';
import { COLUMNS_HEADER } from '../../CreateBulkShipment/constant';
import getFileData, { FileResultType } from '../../../Helper/file';
import { isDutyChargesAllowedSubdomain } from '../../../utils/getSubdomain';
import { SuccessErrorModalOpen } from '../../../store/reducers/SuccessErrorModal';
import { setSelectedUser } from '../../../store/reducers/AccountLookup';
import { isExcelFile } from '../UserDetails/utils/fileType';
import { getUploadUrl, uploadToS3, getUploadUrls, PresignedUrlsResponse } from '../UserDetails/services/uploadService';
import {
  extractDocument,
  extractTextDetails,
  pollExtraction,
  formatInvalidFieldsReason,
  startBatchExtraction,
  ExtractedShipmentFields,
} from '../UserDetails/services/extractionService';
import dayjs from 'dayjs';
import CloseIcon from '@mui/icons-material/Close';
import WarningIcon from '@mui/icons-material/Warning';
import ImportAiModal from '../UserDetails/ImportAiModal';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { getAddressObj, getSingleShipmentPackagePayload } from '../../../services/getShipmentPayload';
import { DraftShipmentCreatePayload } from '@Reducers/DraftShipment/Type';
import { createDraftShipment, updateDraftShipmentById } from '@Reducers/DraftShipment/actions';

const _requireColumnHeader = COLUMNS_HEADER.filter(x => x.require);

interface ShipperConsigneeProps {
  userId?: string;
  accountNumber?: string;
  accountId?: string;
  onBatchModeChange?: (isBatch: boolean) => void;
}

function ShipperConsignee(props: ShipperConsigneeProps): JSX.Element {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();

  const organizationData = useSelector((state: RootStateType) => state.orgSetup.organizationPublicData);
  const { name = '' } = organizationData || {};

  const { selectedUser } = useSelector((state: RootStateType) => state.accountLookup['CreateShipment'] || {});

  const countryHashById = useSelector((state: RootStateType) => state.global.countriesNameObj);
  const { countryList, alternateCountryNameObj } = useSelector((state: RootStateType) => state?.global || {});
  const { shipperAddresses, consigneeAddresses } = useSelector((state: RootStateType) => state?.savedAddress);
  const { shipmentSetting } = useSelector((state: RootStateType) => state?.orgSetup);
  const organizationCountry = useSelector((state: RootStateType) => state.orgSetup.organizationData);
  const pincodeServiceabilityForShipment = useSelector(
    (state: RootStateType) => state?.pincode.pincodeServiceabilityForShipment,
  );
  const { pickupTimeSlots } = shipmentSetting || {};
  const { selectedDraftShipmentId: draftId } = useSelector((state: RootStateType) => state.DraftShipment);

  const {
    setActiveStep,
    shipper,
    setShipper,
    consignee,
    setConsignee,
    pickupType,
    packageData,
    setPackageData,
    shipmentType,
    setShipmentType,
    boxes,
    setBoxes,
    setSelectedBox,
    setSelectedProduct,
    notes,
    extraDetails,
    setIsBatchMode,
    setBulkShipments,
    setBatchCounts,
    setBatchId,
  } = React.useContext(CreateShipmentContext) as createShipmentContextType;

  const [shipperState, setShipperState] = useState<userDetailsType>({ ...defaultShipperState, ...shipper });
  const [consigneeState, setConsigneeState] = useState<userDetailsType>({ ...defaultConsignee, ...consignee });
  const [shipperErrors, setShipperErrors] = useState<userDetailsErrorType>({ ...defaultErrorState });
  const [consigneeErrors, setConsigneeErrors] = useState<userDetailsErrorType>({ ...defaultErrorState });
  const [validationAlert, setValidationAlert] = useState<string[] | null>(null);

  const [shipperSaveAs, setShipperSaveAs] = useState<string>('');
  const [consigneeSaveAs, setConsigneeSaveAs] = useState<string>('');

  const [showShipperAltContact, setShowShipperAltContact] = useState<boolean>(
    Boolean(shipper.altContactName || shipper.altContactNo),
  );
  const [showConsigneeAltContact, setShowConsigneeAltContact] = useState<boolean>(
    Boolean(consignee.altContactName || consignee.altContactNo),
  );

  const [reinitializeShipper, setReinitializeShipper] = useState<boolean>(true);
  const [reinitializeConsignee, setReinitializeConsignee] = useState<boolean>(true);

  // AI Import states
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isImporting, setIsImporting] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [extractionError, setExtractionError] = useState<string | null>(null);
  const isCancelledRef = useRef<boolean>(false);
  const pollingTimerRef = useRef<NodeJS.Timeout | null>(null);
  const [aiReviewNotice, setAiReviewNotice] = useState<string | null>(null);

  useEffect(() => {
    return () => {
      isCancelledRef.current = true;
      if (pollingTimerRef.current) {
        clearTimeout(pollingTimerRef.current);
      }
    };
  }, []);

  const handleSetBatchMode = (val: boolean) => {
    setIsBatchMode(val);
    props.onBatchModeChange?.(val);
  };

  useEffect(() => {
    setShipperState({ ...defaultShipperState, ...shipper });
  }, [shipper]);

  useEffect(() => {
    setConsigneeState({ ...defaultConsignee, ...consignee });
  }, [consignee]);

  const { validationStates, validate } = useAiValidation();

  useEffect(() => {
    if (shipperState.email) {
      validate('email', 'email', shipperState.email);
    }
  }, [shipperState.email, validate]);

  useEffect(() => {
    const countryDisplay = countryHashById?.[shipperState.country]?.name || shipperState.country;
    validate('Shipper.phoneNumber', 'phone', {
      country: countryDisplay || undefined,
      telephoneCode: shipperState.telephoneCode || undefined,
      number: shipperState.phoneNumber || '',
    });
  }, [shipperState.phoneNumber, shipperState.country, shipperState.telephoneCode, countryHashById, validate]);

  useEffect(() => {
    if (shipperState.pincode && shipperState.city && shipperState.state && shipperState.country) {
      const countryDisplay = countryHashById?.[shipperState.country]?.name || shipperState.country;
      validate('Shipper.address', 'shipmentAddress', {
        pincode: shipperState.pincode,
        city: shipperState.city,
        state: shipperState.state,
        country: countryDisplay,
      });
    }
  }, [shipperState.pincode, shipperState.city, shipperState.state, shipperState.country, countryHashById, validate]);

  useEffect(() => {
    const countryDisplay = countryHashById?.[consigneeState.country]?.name || consigneeState.country;
    validate('Consignee.phoneNumber', 'phone', {
      country: countryDisplay || undefined,
      telephoneCode: consigneeState.telephoneCode || undefined,
      number: consigneeState.phoneNumber || '',
    });
  }, [consigneeState.phoneNumber, consigneeState.country, consigneeState.telephoneCode, countryHashById, validate]);

  useEffect(() => {
    if (consigneeState.pincode && consigneeState.city && consigneeState.state && consigneeState.country) {
      const countryDisplay = countryHashById?.[consigneeState.country]?.name || consigneeState.country;
      validate('Consignee.address', 'shipmentAddress', {
        pincode: consigneeState.pincode,
        city: consigneeState.city,
        state: consigneeState.state,
        country: countryDisplay,
      });
    }
  }, [
    consigneeState.pincode,
    consigneeState.city,
    consigneeState.state,
    consigneeState.country,
    countryHashById,
    validate,
  ]);

  const { disablePickup } = usePickupDropoffAccess({ shipperCountry: shipperState.country });

  function formatAddressFromSaved(address: AddressType, current: userDetailsType): userDetailsType {
    const {
      phone,
      alternatePhone,
      name: addrName,
      companyName,
      line1,
      line2,
      city,
      state,
      country,
      pincode,
      email,
    } = address;
    return {
      ...current,
      name: addrName || '',
      email: email || '',
      companyName: companyName || '',
      line1: line1 || '',
      line2: line2 || '',
      city: city || '',
      state: state || '',
      country: country || '',
      pincode: pincode || '',
      phoneNumber: phone?.number ? String(phone.number) : '',
      telephoneCode: phone?.telephoneCode ? String(phone.telephoneCode) : '',
      altContactTelephoneCode: alternatePhone?.telephoneCode ? String(alternatePhone.telephoneCode) : '',
      altContactNo: alternatePhone?.number ? String(alternatePhone.number) : '',
    };
  }

  const handleSelectShipperAddress = (address: AddressType) => {
    const formatted = formatAddressFromSaved(address, shipperState);
    setShipperState(formatted);
    setShipper(formatted);
    setReinitializeShipper(false);
  };

  const handleSelectConsigneeAddress = (address: AddressType) => {
    const formatted = formatAddressFromSaved(address, consigneeState);
    setConsigneeState(formatted);
    setConsignee(formatted);
    setReinitializeConsignee(false);
  };

  const handleShipperChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name: fieldName, value } = event.target;
    const sanitized = fieldName === 'phoneNumber' || fieldName === 'altContactNo' ? value.replace(/[^\d]/g, '') : value;
    const updated = { ...shipperState, [fieldName]: sanitized };
    setShipperState(updated);
    setShipper(updated);

    if (shipperErrors[fieldName as keyof userDetailsErrorType]) {
      setShipperErrors({ ...shipperErrors, [fieldName]: '' });
    }
  };

  const handleShipperDropdownChange = (
    _selected: Array<string>,
    _selectedObj: DropdownOptionType[],
    event: SelectChangeEvent<string>,
  ) => {
    const { name: fieldName, value } = event.target;
    const updated = { ...shipperState, [fieldName]: value };
    setShipperState(updated);
    setShipper(updated);
    if (shipperErrors[fieldName as keyof userDetailsErrorType]) {
      setShipperErrors({ ...shipperErrors, [fieldName]: '' });
    }
  };

  const handleShipperGoogleAddress = (event: any) => {
    const address = getFormattedLocation(event.address_components);
    const updated = { ...shipperState, ...address };
    setShipperState(updated);
    setShipper(updated);
    setReinitializeShipper(false);
  };

  const handleConsigneeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name: fieldName, value } = event.target;
    const sanitized = fieldName === 'phoneNumber' || fieldName === 'altContactNo' ? value.replace(/[^\d]/g, '') : value;
    const updated = { ...consigneeState, [fieldName]: sanitized };
    setConsigneeState(updated);
    setConsignee(updated);

    if (consigneeErrors[fieldName as keyof userDetailsErrorType]) {
      setConsigneeErrors({ ...consigneeErrors, [fieldName]: '' });
    }
  };

  const handleConsigneeDropdownChange = (
    _selected: Array<string>,
    _selectedObj: DropdownOptionType[],
    event: SelectChangeEvent<string>,
  ) => {
    const { name: fieldName, value } = event.target;
    const updated = { ...consigneeState, [fieldName]: value };
    setConsigneeState(updated);
    setConsignee(updated);
    if (consigneeErrors[fieldName as keyof userDetailsErrorType]) {
      setConsigneeErrors({ ...consigneeErrors, [fieldName]: '' });
    }
  };

  const handleConsigneeGoogleAddress = (event: any) => {
    const address = getFormattedLocation(event.address_components);
    const updated = { ...consigneeState, ...address };
    setConsigneeState(updated);
    setConsignee(updated);
    setReinitializeConsignee(false);
  };

  function saveOrUpdateAddress(details: userDetailsType, type: 'shipper' | 'consignee', label: string) {
    if (!label) return;
    const addressObj: AddressType = {
      type,
      phone: {
        telephoneCode: details.telephoneCode,
        number: details.phoneNumber,
      },
      name: details.name,
      email: details.email,
      companyName: details.companyName,
      line1: details.line1,
      line2: details.line2,
      city: details.city,
      state: details.state,
      country: details.country,
      _countryId: countryHashById?.[details.country]?._id,
      pincode: details.pincode,
      label,
    };
    if (details.altContactNo) {
      addressObj.alternatePhone = {
        telephoneCode: details.altContactTelephoneCode,
        number: details.altContactNo,
      };
    }
    dispatch(addAddress({ payload: addressObj }));
  }

  const handleValidateAndContinue = (event: React.SyntheticEvent) => {
    event.preventDefault();

    const defaultTel = organizationCountry?.contactInfo?.telephoneCode?.toString() || '';
    const cleanShipper = {
      ...shipperState,
      telephoneCode: shipperState.telephoneCode || defaultTel,
      altContactTelephoneCode: shipperState.altContactTelephoneCode || defaultTel,
    };
    const cleanConsignee = {
      ...consigneeState,
      telephoneCode: consigneeState.telephoneCode || defaultTel,
      altContactTelephoneCode: consigneeState.altContactTelephoneCode || defaultTel,
    };

    setShipperState(cleanShipper);
    setConsigneeState(cleanConsignee);

    const shipperResult: ValidationResult = ShipmentUserSchema.validate(cleanShipper, { abortEarly: false });
    const sErrors: userDetailsErrorType = { ...defaultErrorState };
    if (shipperResult.error) {
      for (const item of shipperResult.error.details) {
        const fieldName = item.path[0];
        sErrors[fieldName as keyof userDetailsErrorType] = item.message;
      }
    }

    if (name !== 'Cargo Solutions Network') {
      if (pickupType.type === 'dropOff' && !pickupType.warehouse) {
        sErrors['warehouse'] = 'Please choose the drop-off warehouse.';
      } else if (pickupType.type === 'pickUp') {
        if (!pickupType.date) {
          sErrors['date'] = 'Please choose a pickup date.';
        }
        if (pickupTimeSlots?.slots?.length && !pickupType.timeSlot) {
          sErrors['timeSlot'] = 'Please choose a time slot.';
        }
      }
    }

    const consigneeResult: ValidationResult = ShipmentUserSchema.validate(cleanConsignee, { abortEarly: false });
    const cErrors: userDetailsErrorType = { ...defaultErrorState };
    if (consigneeResult.error) {
      for (const item of consigneeResult.error.details) {
        const fieldName = item.path[0];
        cErrors[fieldName as keyof userDetailsErrorType] = item.message;
      }
    }

    // Strict country enforcement: must be non-empty and exist in available countryList
    const validCountryNames = new Set((countryList || []).map(c => c.value.toLowerCase()));
    if (!cleanShipper.country || !validCountryNames.has(cleanShipper.country.toLowerCase())) {
      sErrors['country'] = 'Please select a valid shipper country.';
    }
    if (!cleanConsignee.country || !validCountryNames.has(cleanConsignee.country.toLowerCase())) {
      cErrors['country'] = 'Please select a valid destination country.';
    }

    setShipperErrors(sErrors);
    setConsigneeErrors(cErrors);

    const hasShipperError = Object.values(sErrors).some(Boolean);
    const hasConsigneeError = Object.values(cErrors).some(Boolean);

    if (hasShipperError || hasConsigneeError) {
      const priorityFields: { key: string; scope: 'shipper' | 'consignee'; id: string; label: string }[] = [
        { key: 'name', scope: 'shipper', id: 'shipper-name', label: 'Shipper Full Name' },
        { key: 'phoneNumber', scope: 'shipper', id: 'shipper-phone', label: 'Shipper Mobile Number' },
        { key: 'line1', scope: 'shipper', id: 'shipper-line1', label: 'Shipper Address Line 1' },
        { key: 'city', scope: 'shipper', id: 'shipper-city', label: 'Shipper City' },
        { key: 'country', scope: 'shipper', id: 'shipper-country-select', label: 'Shipper Country' },
        { key: 'pincode', scope: 'shipper', id: 'shipper-pincode', label: 'Shipper Pincode' },
        { key: 'warehouse', scope: 'shipper', id: 'warehouse', label: 'Drop-off Warehouse' },
        { key: 'date', scope: 'shipper', id: 'pickup-date', label: 'Pickup Date' },
        { key: 'timeSlot', scope: 'shipper', id: 'timeSlot', label: 'Pickup Time Slot' },
        { key: 'name', scope: 'consignee', id: 'consignee-name', label: 'Consignee Full Name' },
        { key: 'phoneNumber', scope: 'consignee', id: 'consignee-phone', label: 'Consignee Mobile Number' },
        { key: 'country', scope: 'consignee', id: 'consignee-country-select', label: 'Consignee Country' },
        { key: 'line1', scope: 'consignee', id: 'consignee-line1', label: 'Consignee Address Line 1' },
        { key: 'city', scope: 'consignee', id: 'consignee-city', label: 'Consignee City' },
        { key: 'pincode', scope: 'consignee', id: 'consignee-pincode', label: 'Consignee Pincode' },
      ];

      const missingList: string[] = [];
      let firstElementId: string | null = null;

      priorityFields.forEach(field => {
        const err =
          field.scope === 'shipper'
            ? sErrors[field.key as keyof userDetailsErrorType]
            : cErrors[field.key as keyof userDetailsErrorType];
        if (err) {
          missingList.push(field.label);
          if (!firstElementId) {
            firstElementId = field.id;
          }
        }
      });

      // Include other custom errors if present
      Object.entries(sErrors).forEach(([k, msg]) => {
        if (msg && !priorityFields.some(p => p.scope === 'shipper' && p.key === k)) {
          missingList.push(`Shipper ${k}`);
          if (!firstElementId) firstElementId = 'shipper-name';
        }
      });
      Object.entries(cErrors).forEach(([k, msg]) => {
        if (msg && !priorityFields.some(p => p.scope === 'consignee' && p.key === k)) {
          missingList.push(`Consignee ${k}`);
          if (!firstElementId) firstElementId = 'consignee-name';
        }
      });

      setValidationAlert(missingList);

      const previewText =
        missingList.slice(0, 3).join(', ') + (missingList.length > 3 ? ` and ${missingList.length - 3} more` : '');
      enqueueSnackbar(`Please complete all required fields: ${previewText}`, { variant: 'error' });

      if (firstElementId) {
        setTimeout(() => {
          const el = document.getElementById(firstElementId!);
          if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            const input = el.querySelector('input, select, textarea') as HTMLElement | null;
            if (input && typeof input.focus === 'function') {
              input.focus();
            } else if (typeof el.focus === 'function') {
              el.focus();
            }
          }
        }, 100);
      }
    } else {
      setValidationAlert(null);
      if (shipperSaveAs) saveOrUpdateAddress(cleanShipper, 'shipper', shipperSaveAs);
      if (consigneeSaveAs) saveOrUpdateAddress(cleanConsignee, 'consignee', consigneeSaveAs);

      setShipper(cleanShipper);
      setConsignee(cleanConsignee);
      setActiveStep(1);
    }
  };

  const enableSaveAsDraft = useMemo(() => {
    return Boolean(shipperState?.name || shipperState?.phoneNumber);
  }, [shipperState?.name, shipperState?.phoneNumber]);

  const handleSaveDraft = () => {
    if (!enableSaveAsDraft) return;

    const defaultTel = organizationCountry?.contactInfo?.telephoneCode?.toString() || '';
    const cleanShipper = {
      ...shipperState,
      telephoneCode: shipperState.telephoneCode || defaultTel,
      altContactTelephoneCode: shipperState.altContactTelephoneCode || defaultTel,
    };
    const cleanConsignee = {
      ...consigneeState,
      telephoneCode: consigneeState.telephoneCode || defaultTel,
      altContactTelephoneCode: consigneeState.altContactTelephoneCode || defaultTel,
    };

    const packageObj = getSingleShipmentPackagePayload({
      boxes,
      packageData,
      shipmentType,
      pickupType,
    });

    const postObj: DraftShipmentCreatePayload = {
      package: packageObj,
      shipper: getAddressObj(cleanShipper),
      consignee: getAddressObj(cleanConsignee),
      shipmentNotes: notes,
      shipmentMode: shipmentType.shipmentMode,
      extraDetails: {
        shipper: {
          type: cleanShipper.type,
          IdType: cleanShipper.IdType,
          IdNumber: cleanShipper.IdNumber,
        },
        consignee: {
          type: cleanConsignee.type,
          IdType: cleanConsignee.IdType,
          IdNumber: cleanConsignee.IdNumber,
        },
        other: extraDetails,
      },
      _onbehalf: props?.userId ? props?.userId : null,
      _accountId: props.accountId,
      accountNumber: props.accountNumber,
    };

    if (pickupType.type === 'dropOff') {
      postObj._warehouseId = pickupType.warehouse;
    }

    if (draftId) {
      dispatch(
        updateDraftShipmentById({
          id: draftId,
          payload: postObj,
          navigate: '/shipment/draft?tab=saved',
          showModal: true,
        }),
      );
    } else {
      dispatch(createDraftShipment({ payload: postObj, navigate: '/shipment/draft?tab=saved', showModal: true }));
    }
  };

  const handleAiImportClick = () => {
    isCancelledRef.current = false;
    setExtractionError(null);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    isCancelledRef.current = true;
    if (pollingTimerRef.current) {
      clearTimeout(pollingTimerRef.current);
      pollingTimerRef.current = null;
    }
    setIsImporting(false);
    setIsModalOpen(false);
  };

  const populateFormFromExtractionFields = (fields: ExtractedShipmentFields) => {
    const getCountryName = (cName?: string) => {
      if (!cName || !String(cName).trim()) return '';
      const trimmed = String(cName).trim();
      const match = (countryList || []).find(
        c => c.value.toLowerCase() === trimmed.toLowerCase() || c.display.toLowerCase() === trimmed.toLowerCase(),
      );
      if (match) return match.value;

      const altMatch = alternateCountryNameObj?.[trimmed.toLowerCase()];
      if (altMatch?.name) {
        const matchedInList = (countryList || []).find(
          c =>
            c.value.toLowerCase() === altMatch.name.toLowerCase() ||
            c.display.toLowerCase() === altMatch.name.toLowerCase(),
        );
        if (matchedInList) return matchedInList.value;
        return altMatch.name;
      }

      return '';
    };

    const cleanPhoneCode = (code?: string) => {
      if (!code) return '';
      const clean = code.trim();
      return clean.startsWith('+') ? clean : `+${clean}`;
    };

    const sanitizePhone = (num?: string): string => (num ? String(num).replace(/[^\d]/g, '') : '');

    const shipperFields = fields.shipper
      ? {
          name: fields.shipper.name || fields.shipper.companyName || '',
          companyName: fields.shipper.companyName || '',
          line1: fields.shipper.line1 || '',
          line2: fields.shipper.line2 || '',
          city: fields.shipper.city || '',
          state: fields.shipper.state || '',
          country: getCountryName(fields.shipper.country ? String(fields.shipper.country) : undefined),
          pincode: fields.shipper.pincode || '',
          phoneNumber: sanitizePhone(fields.shipper.phone?.number || fields.shipper.phoneNumber),
          telephoneCode: cleanPhoneCode(fields.shipper.phone?.telephoneCode || fields.shipper.telephoneCode),
          email: fields.shipper.email || '',
        }
      : {};

    const newShipper = {
      ...defaultShipperState,
      ...shipperFields,
    };
    setShipper(newShipper);
    setShipperState(newShipper);

    const consigneeFields = fields.consignee
      ? {
          name: fields.consignee.name || fields.consignee.companyName || '',
          companyName: fields.consignee.companyName || '',
          line1: fields.consignee.line1 || '',
          line2: fields.consignee.line2 || '',
          city: fields.consignee.city || '',
          state: fields.consignee.state || '',
          country: getCountryName(fields.consignee.country ? String(fields.consignee.country) : undefined),
          pincode: fields.consignee.pincode || '',
          phoneNumber: sanitizePhone(fields.consignee.phone?.number || fields.consignee.phoneNumber),
          telephoneCode: cleanPhoneCode(fields.consignee.phone?.telephoneCode || fields.consignee.telephoneCode),
          email: fields.consignee.email || '',
        }
      : {};

    const newConsignee = {
      ...defaultConsignee,
      ...consigneeFields,
    };
    setConsignee(newConsignee);
    setConsigneeState(newConsignee);

    const extractedInvoiceNumber = fields.package?.invoiceNumber || fields.invoiceNumber || '';

    if (fields.package) {
      const p = fields.package;
      setPackageData({
        ...defaultPackageState,
        currency: p.currency || '',
        isDocument: typeof p.isDocument === 'boolean' ? p.isDocument : false,
        userReferenceNumber: fields.userReferenceNumber || '',
        invoiceNumber: extractedInvoiceNumber,
      });

      setShipmentType({
        ...defaultShipmentTypeState,
        currency: p.currency || '',
        type: p.isDocument ? 'document' : 'parcel',
        referenceNumber: fields.userReferenceNumber || '',
        invoiceNumber: extractedInvoiceNumber,
      });

      const rawBoxes = p.box || p.boxes || fields?.boxes || fields?.box;
      const boxesList: any[] = Array.isArray(rawBoxes)
        ? rawBoxes
        : rawBoxes && typeof rawBoxes === 'object'
        ? [rawBoxes]
        : [];

      if (boxesList.length > 0) {
        const parsedBoxes = boxesList.map((b: any, index: number) => {
          const boxProducts = (b.products || []).map((prod: any, prodIndex: number) => {
            const rawQty = prod.quantity ?? prod.qty ?? prod.Quantity ?? prod.Qty;
            const quantity = rawQty !== undefined && rawQty !== null && rawQty !== '' ? Number(rawQty) : null;
            const rawUnitVal = prod.unitValue ?? prod.value ?? prod.UnitValue ?? prod.Value;
            const unitValue =
              rawUnitVal !== undefined && rawUnitVal !== null && rawUnitVal !== '' ? Number(rawUnitVal) : null;
            const totalVal = quantity !== null && unitValue !== null ? quantity * unitValue : 0;
            return {
              id: prodIndex.toString(),
              description: prod.description || prod.Description || '',
              quantity,
              unitValue,
              HSNName: prod.HSNName || prod.hsnName || '',
              HSNCode: prod.HSNCode ? String(prod.HSNCode) : prod.hsnCode ? String(prod.hsnCode) : '',
              productId: prod.productId ? String(prod.productId).trim() : '',
              productIdType: prod.productIdType ? String(prod.productIdType).trim() : 'SKU',
              merchantProductId: prod.merchantProductId ? String(prod.merchantProductId).trim() : '',
              standardManufacturerProductId: prod.standardManufacturerProductId
                ? String(prod.standardManufacturerProductId).trim()
                : '',
              nonStandardManufacturerProductId: prod.nonStandardManufacturerProductId
                ? String(prod.nonStandardManufacturerProductId).trim()
                : '',
              totalValue: totalVal,
              actions: null,
            };
          });

          return {
            id: index.toString(),
            length: b.length ? String(b.length) : '',
            width: b.width ? String(b.width) : '',
            height: b.height ? String(b.height) : '',
            weight: b.weight ? String(b.weight) : '',
            boxCopyCount: b.boxCopyCount !== undefined ? Number(b.boxCopyCount) : 0,
            products: boxProducts,
            selected: true,
          };
        });

        if (parsedBoxes.length > 0) {
          setBoxes(parsedBoxes);
          setSelectedBox(parsedBoxes[0]);
          if (parsedBoxes[0].products && parsedBoxes[0].products.length > 0) {
            setSelectedProduct(parsedBoxes[0].products[0]);
          }
        }
      }
    } else {
      setPackageData({ ...defaultPackageState, invoiceNumber: extractedInvoiceNumber });
      setShipmentType({ ...defaultShipmentTypeState, invoiceNumber: extractedInvoiceNumber });
      setBoxes(structuredClone(defaultBoxesState));
    }
  };

  const performTextExtraction = async (text: string) => {
    if (!text.trim()) {
      setExtractionError('Please enter some shipment details.');
      return;
    }
    isCancelledRef.current = false;
    setIsImporting(true);
    setExtractionError(null);
    try {
      const initialResponse = await extractTextDetails(text);
      if (isCancelledRef.current) return;

      const response = await pollExtraction(initialResponse, {
        isCancelled: () => isCancelledRef.current,
        registerTimer: timer => {
          pollingTimerRef.current = timer;
        },
      });
      if (isCancelledRef.current) return;

      if (response.status === 'completed' || (!response.status && response.fields)) {
        if (response.fields && Object.keys(response.fields).length > 0) {
          populateFormFromExtractionFields(response.fields);
          setAiReviewNotice(null);
          setIsModalOpen(false);
        } else {
          setExtractionError('The extraction service did not return any fields.');
        }
      } else if (response.status === 'needs_review') {
        const reviewReason = formatInvalidFieldsReason(response.invalidFields) || response.error || '';
        if (response.fields && Object.keys(response.fields).length > 0) {
          populateFormFromExtractionFields(response.fields);
          setAiReviewNotice(reviewReason || 'Some extracted details require your review before continuing.');
          setIsModalOpen(false);
        } else {
          setExtractionError(
            reviewReason || response.error || 'Document extraction requires review. Please enter details manually.',
          );
        }
      } else if (response.status === 'failed' || response.error) {
        setExtractionError(response.error || 'Failed to extract text details.');
      } else {
        setExtractionError(response.error || `Unexpected extraction status: ${response.status}`);
      }
    } catch (error: any) {
      if (isCancelledRef.current) return;
      console.error('❌ Text Extraction Failed:', error);
      setExtractionError(error.message || 'Failed to extract text details.');
    } finally {
      if (!isCancelledRef.current) {
        setIsImporting(false);
      }
    }
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const fileList = event.target.files;
    if (!fileList || fileList.length === 0) return;

    const selectedFiles = Array.from(fileList);
    event.target.value = '';

    const excelFiles = selectedFiles.filter(isExcelFile);

    if (excelFiles.length > 1 || (excelFiles.length === 1 && selectedFiles.length > 1)) {
      setExtractionError('Only 1 Excel file can be uploaded at a time. Please upload a single Excel file.');
      return;
    }

    if (selectedFiles.length > 100) {
      setExtractionError('Maximum 100 files can be uploaded at a time.');
      return;
    }

    setIsImporting(true);
    setExtractionError(null);

    // 1. Single Excel file -> Existing Excel Bulk Upload Workflow (NO AI EXTRACTION)
    if (excelFiles.length === 1) {
      const file = excelFiles[0];
      try {
        const fileResult = (await getFileData([file], 'xls')) as FileResultType[];
        const [{ fileData }] = fileResult;
        if (!(fileData instanceof Array)) {
          setExtractionError('Unable to read the Excel file.');
          setIsImporting(false);
          return;
        }

        const shipmentSheet = fileData.find((sheet: any) => sheet.sheetName?.toLowerCase() === 'shipment');
        if (!shipmentSheet) {
          setExtractionError('Oops! "shipment" sheet not found in the Excel file.');
          setIsImporting(false);
          return;
        }

        const [header] = shipmentSheet.data;
        if (!Array.isArray(header)) {
          setExtractionError('Invalid sheet header format.');
          setIsImporting(false);
          return;
        }

        const requiredCols =
          name === 'Cargo Solutions Network' || !isDutyChargesAllowedSubdomain()
            ? _requireColumnHeader.filter(currCol => currCol.key !== 'dutyCharge')
            : _requireColumnHeader;

        const invalidCols = requiredCols.filter(currCol => {
          const isValid = header.find((x: string) => currCol.regEx.test(x));
          return !isValid;
        });

        if (invalidCols.length) {
          const missingHeaderCols = invalidCols.map(x => x.label).join(', ');
          setExtractionError(`Column header "${missingHeaderCols}" is missing from your sheet.`);
          setIsImporting(false);
          return;
        }

        const dutyChargeConfig = COLUMNS_HEADER.find(c => c.key === 'dutyCharge');
        const dutyChargeColIndex = dutyChargeConfig
          ? header.findIndex((h: string) => dutyChargeConfig.regEx.test(h))
          : -1;

        const shipperNameConfig = COLUMNS_HEADER.find(c => c.key === 'shipperName');
        const shipperNameIndex = shipperNameConfig
          ? header.findIndex((h: string) => shipperNameConfig.regEx.test(h))
          : -1;

        if (
          name !== 'Cargo Solutions Network' &&
          isDutyChargesAllowedSubdomain() &&
          dutyChargeColIndex !== -1 &&
          shipperNameIndex !== -1
        ) {
          const invalidRows: number[] = [];
          for (let i = 1; i < shipmentSheet.data.length; i++) {
            const row = shipmentSheet.data[i];
            if (!Array.isArray(row) || row.length === 0) continue;
            const shipperName = row[shipperNameIndex];
            if (shipperName !== undefined && shipperName !== null && String(shipperName).trim() !== '') {
              const dutyValue = row[dutyChargeColIndex];
              const dutyValueStr = dutyValue !== undefined && dutyValue !== null ? String(dutyValue).trim() : '';
              if (dutyValueStr !== '') {
                const isValidDuty = /^(DDU|DDP)$/i.test(dutyValueStr);
                if (!isValidDuty) {
                  invalidRows.push(i + 1);
                }
              }
            }
          }

          if (invalidRows.length > 0) {
            setExtractionError(
              `Invalid value for "Duty & Tax Billed (DDU / DDP)" at row(s): ${invalidRows.join(
                ', ',
              )}. Value must be either "DDU" or "DDP".`,
            );
            setIsImporting(false);
            return;
          }
        }

        if (name === 'Cargo Solutions Network' && shipmentSheet.data.length >= 452) {
          dispatch(
            SuccessErrorModalOpen({
              type: 'error',
              title: 'Limit Exceeded',
              subTitle: 'Maximum 450 Shipments allowed.',
            }),
          );
          setIsImporting(false);
          return;
        }

        if (selectedUser) {
          dispatch(setSelectedUser(['createBulkShipment', selectedUser]));
        }
        setIsModalOpen(false);
        setIsImporting(false);
        navigate('/shipment/create-bulk/sheet-match-header', {
          state: {
            sheetData: shipmentSheet.data,
            selectedUser,
            userId: props.userId || selectedUser?._id,
          },
        });
        return;
      } catch (err: any) {
        console.error('❌ Failed to process Excel file:', err);
        setExtractionError(err?.message || 'Failed to process Excel file.');
        setIsImporting(false);
        return;
      }
    }

    // 2. Multiple non-Excel files -> AI Multiple-File Batch Extraction Workflow
    if (selectedFiles.length > 1) {
      isCancelledRef.current = false;

      const filesPayload = selectedFiles.map(f => ({
        fileName: f.name,
        mimeType: f.type || 'application/octet-stream',
      }));

      let uploadUrlsResponse: PresignedUrlsResponse;
      try {
        uploadUrlsResponse = await getUploadUrls(filesPayload);
      } catch (error: any) {
        console.error('❌ Multi-upload URL Request Failed:', error);
        setExtractionError(error.message || 'Failed to request upload URLs.');
        setIsImporting(false);
        return;
      }

      if (!uploadUrlsResponse || !uploadUrlsResponse.batchId || !Array.isArray(uploadUrlsResponse.files)) {
        console.error('❌ Unexpected upload-urls response:', uploadUrlsResponse);
        setExtractionError('The upload URLs response is incomplete or invalid.');
        setIsImporting(false);
        return;
      }

      if (uploadUrlsResponse.files.length !== selectedFiles.length) {
        console.error('❌ Selected files vs response length mismatch:', uploadUrlsResponse);
        setExtractionError('A different number of upload URLs than requested was returned.');
        setIsImporting(false);
        return;
      }

      try {
        await Promise.all(
          selectedFiles.map((file, index) => {
            const uploadInfo = uploadUrlsResponse.files[index];
            return uploadToS3(uploadInfo, file);
          }),
        );
      } catch (error: any) {
        console.error('❌ S3 Batch Upload Failed:', error);
        setExtractionError(error.message || 'Failed to upload all files to S3.');
        setIsImporting(false);
        return;
      }

      const batchKeys = uploadUrlsResponse.files.map(f => f.key);
      try {
        await startBatchExtraction(uploadUrlsResponse.batchId, batchKeys);
      } catch (error: any) {
        console.error('❌ Batch Extraction Request Failed:', error);
        setExtractionError(error.message || 'Failed to initiate batch extraction.');
        setIsImporting(false);
        return;
      }

      const initialPendingShipments = selectedFiles.map((file, idx) => ({
        jobId: uploadUrlsResponse.files[idx]?.key || `job-${idx}`,
        status: 'pending',
        isSelected: true,
        carrierPreference: 'cheapest',
        package: {
          invoiceNumber: file.name || `Shipment ${idx + 1}`,
          unitOfMeasurement: '',
          packaging: false,
          insurance: false,
          isDocument: false,
          currency: '',
          shipmentMode: '',
          isCod: false,
          isDropOff: false,
          pickupDate: dayjs(),
          timeSlot: '',
          codAmount: 0,
          box: [],
          totalBoxWeight: 0,
          userReferenceNumber: '',
        },
        shipper: {} as any,
        consignee: {} as any,
      }));

      setBulkShipments(initialPendingShipments);
      setBatchCounts({ pending: selectedFiles.length, completed: 0, failed: 0, needs_review: 0 });
      setBatchId(uploadUrlsResponse.batchId);
      setIsModalOpen(false);
      handleSetBatchMode(true);
      setIsImporting(false);
      return;
    }

    // 3. Single non-Excel file -> AI Single-File Extraction Workflow
    isCancelledRef.current = false;
    const file = selectedFiles[0];

    let uploadUrlResponse;
    try {
      uploadUrlResponse = await getUploadUrl(file);
    } catch (error: any) {
      setExtractionError(error.message || 'Failed to request upload URL.');
      setIsImporting(false);
      return;
    }

    if (!uploadUrlResponse || !uploadUrlResponse.url || !uploadUrlResponse.fields || !uploadUrlResponse.key) {
      setExtractionError('The upload URL response is incomplete or invalid.');
      setIsImporting(false);
      return;
    }

    try {
      await uploadToS3(uploadUrlResponse, file);
    } catch (error: any) {
      setExtractionError(error.message || 'Failed to upload file to S3.');
      setIsImporting(false);
      return;
    }

    try {
      const initialResponse = await extractDocument(uploadUrlResponse.key);
      if (isCancelledRef.current) return;

      const response = await pollExtraction(initialResponse, {
        isCancelled: () => isCancelledRef.current,
        registerTimer: timer => {
          pollingTimerRef.current = timer;
        },
      });
      if (isCancelledRef.current) return;

      if (response.status === 'completed' || (!response.status && response.fields)) {
        if (response.fields && Object.keys(response.fields).length > 0) {
          populateFormFromExtractionFields(response.fields);
          setAiReviewNotice(null);
          setIsModalOpen(false);
        } else {
          setExtractionError('The extraction service did not return any fields.');
        }
      } else if (response.status === 'needs_review') {
        const reviewReason = formatInvalidFieldsReason(response.invalidFields) || response.error || '';
        if (response.fields && Object.keys(response.fields).length > 0) {
          populateFormFromExtractionFields(response.fields);
          setAiReviewNotice(reviewReason || 'Some extracted details require your review before continuing.');
          setIsModalOpen(false);
        } else {
          setExtractionError(
            reviewReason || response.error || 'Document extraction requires review. Please enter details manually.',
          );
        }
      } else if (response.status === 'failed' || response.error) {
        setExtractionError(response.error || 'Failed to extract document details.');
      } else {
        setExtractionError(response.error || `Unexpected extraction status: ${response.status}`);
      }
    } catch (error: any) {
      if (isCancelledRef.current) return;
      console.error('❌ Extraction Failed:', error);
      setExtractionError(error.message || 'Failed to extract document details.');
    } finally {
      if (!isCancelledRef.current) {
        setIsImporting(false);
      }
    }
  };

  return (
    <Box sx={{ width: '100%', py: 1 }}>
      {/* Top Section Header & Utility Toolbar */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', sm: 'center' },
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 2,
          mb: 3,
        }}
      >
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 600, color: 'text.primary', mb: 0.5 }}>
            Shipper & Consignee Details
          </Typography>
          <Typography sx={{ fontSize: '14px', fontWeight: 400, color: 'text.secondary' }}>
            Provide pickup sender details and delivery recipient information.
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <SbButton
            variant="outlined"
            onClick={handleAiImportClick}
            disabled={isImporting}
            startIcon={<AutoAwesomeIcon sx={{ color: 'primary.main' }} />}
            sx={{
              borderRadius: '10px !important',
              height: '42px',
              px: 2,
              fontWeight: 600,
              fontSize: '0.875rem',
              borderColor: alpha(theme.palette.primary.main, 0.4),
              color: 'primary.main',
              bgcolor: alpha(theme.palette.primary.main, 0.04),
              '&:hover': {
                borderColor: 'primary.main',
                bgcolor: alpha(theme.palette.primary.main, 0.08),
              },
            }}
          >
            {isImporting ? 'Analyzing Document...' : 'Import with AI'}
          </SbButton>
          <input
            type="file"
            ref={fileInputRef}
            style={{ display: 'none' }}
            onChange={handleFileChange}
            accept=".xlsx,.xls,.pdf,.png,.jpg,.jpeg,.doc,.docx,.txt,.csv"
            multiple
          />
          <ImportAiModal
            open={isModalOpen}
            onClose={handleModalClose}
            isImporting={isImporting}
            onUploadClick={() => fileInputRef.current?.click()}
            onExtractText={performTextExtraction}
            error={extractionError}
            setError={setExtractionError}
          />
        </Box>
      </Box>

      {/* AI Review Notice Banner */}
      {aiReviewNotice && (
        <Paper
          elevation={0}
          sx={{
            mb: 3,
            p: 2,
            borderRadius: '12px',
            bgcolor: alpha(theme.palette.warning.main, 0.08),
            border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`,
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 1.5,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
            <WarningIcon sx={{ color: theme.palette.warning.main, mt: 0.25 }} />
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: theme.palette.warning.dark || '#b78103' }}>
                AI Extraction Needs Review
              </Typography>
              <Typography variant="body2" sx={{ color: theme.palette.text.primary, mt: 0.5 }}>
                {aiReviewNotice}
              </Typography>
            </Box>
          </Box>
          <IconButton size="small" onClick={() => setAiReviewNotice(null)} sx={{ color: theme.palette.text.secondary }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Paper>
      )}

      {/* Main Form: 2-Column Responsive Layout for Desktop / Stacked for Mobile */}
      <Grid container spacing={3}>
        {/* ===================== SHIPPER CARD (SENDER) ===================== */}
        <Grid item xs={12} lg={6}>
          <Card
            elevation={0}
            sx={{
              height: '100%',
              borderRadius: '14px',
              border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.04), 0 2px 8px -2px rgba(0, 0, 0, 0.03)',
              display: 'flex',
              flexDirection: 'column',
              bgcolor: theme.palette.background.paper,
            }}
          >
            {/* Shipper Card Header */}
            <Box
              sx={{
                p: { xs: 1.5, sm: 2 },
                borderBottom: `1px solid ${alpha(theme.palette.divider, 0.06)}`,
                bgcolor: alpha(theme.palette.primary.main, 0.02),
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 1.5,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: '10px',
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    color: 'primary.main',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <LocalShippingOutlinedIcon />
                </Box>
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography sx={{ fontSize: '16px', fontWeight: 500, color: 'primary.main' }}>
                      Shipper Information
                    </Typography>
                    <Chip
                      label="Origin"
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: '0.6875rem',
                        fontWeight: 600,
                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                        color: 'primary.main',
                      }}
                    />
                  </Box>
                  <Typography sx={{ fontSize: '14px', fontWeight: 400, color: 'text.secondary' }}>
                    Sender details & dispatch location
                  </Typography>
                </Box>
              </Box>

              <SavedAddresses addresses={shipperAddresses || []} setSelectedAddress={handleSelectShipperAddress} />
            </Box>

            {/* Shipper Form Body */}
            <CardContent sx={{ p: { xs: 1.5, sm: 2 }, flexGrow: 1 }}>
              <Grid container spacing={2}>
                {/* Full Name & Company Name */}
                <Grid item xs={12} sm={6}>
                  <SbTextField
                    id="shipper-name"
                    label="Shipper Full Name"
                    placeholder="Enter full name"
                    name="name"
                    required
                    value={shipperState.name}
                    onChange={handleShipperChange}
                    error={shipperErrors.name}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <SbTextField
                    id="shipper-company"
                    label="Company Name"
                    placeholder="Enter company name"
                    name="companyName"
                    value={shipperState.companyName}
                    onChange={handleShipperChange}
                    error={shipperErrors.companyName}
                  />
                </Grid>

                {/* Mobile & Email */}
                <Grid item xs={12} sm={6}>
                  <Contact
                    id="shipper-phone"
                    label="Mobile Number *"
                    phoneName="phoneNumber"
                    phoneNumber={shipperState.phoneNumber || ''}
                    onChangePhoneNumber={handleShipperChange}
                    telCountryCodeName="telephoneCode"
                    telCountryCode={shipperState.telephoneCode || ''}
                    onChangeTelCountryCode={handleShipperChange}
                    setDefaultTelephone={(val: number) => {
                      const updated = { ...shipperState, telephoneCode: val.toString() };
                      setShipperState(updated);
                      setShipper(updated);
                    }}
                    error={shipperErrors.phoneNumber || shipperErrors.telephoneCode}
                    validationState={validationStates['Shipper.phoneNumber'] || validationStates['phoneNumber']}
                    onApplySuggestion={suggestion => {
                      const updated = { ...shipperState, phoneNumber: suggestion };
                      setShipperState(updated);
                      setShipper(updated);
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <SbTextField
                    id="shipper-email"
                    label="Email Address"
                    placeholder="name@example.com"
                    name="email"
                    value={shipperState.email}
                    onChange={handleShipperChange}
                    error={shipperErrors.email}
                    validationState={validationStates['email']}
                    onApplySuggestion={suggestion => {
                      const updated = { ...shipperState, email: suggestion };
                      setShipperState(updated);
                      setShipper(updated);
                    }}
                  />
                </Grid>

                {/* Google Maps Search */}
                <Grid item xs={12}>
                  <GoogleMaps
                    onSelect={handleShipperGoogleAddress}
                    label="Search Address Location"
                    placeholder="Start typing street or locality..."
                    reinitialize={reinitializeShipper}
                  />
                </Grid>

                {/* Address Line 1 & Line 2 */}
                <Grid item xs={12} sm={6}>
                  <SbTextField
                    id="shipper-line1"
                    label="Address Line 1"
                    placeholder="Flat / Building / Street"
                    required
                    name="line1"
                    value={shipperState.line1}
                    onChange={handleShipperChange}
                    error={shipperErrors.line1}
                    showCharCount
                    helperText={`${shipperState.line1 || ''}`}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <SbTextField
                    id="shipper-line2"
                    label="Address Line 2"
                    placeholder="Area / Landmark (Optional)"
                    name="line2"
                    value={shipperState.line2}
                    onChange={handleShipperChange}
                    error={shipperErrors.line2}
                    showCharCount
                    helperText={`${shipperState.line2 || ''}`}
                  />
                </Grid>

                {/* City & State */}
                <Grid item xs={12} sm={6}>
                  <SbTextField
                    id="shipper-city"
                    label="City"
                    placeholder="Enter city"
                    required
                    name="city"
                    value={shipperState.city}
                    onChange={handleShipperChange}
                    error={shipperErrors.city}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <SbTextField
                    id="shipper-state"
                    label="State"
                    placeholder="Enter state"
                    name="state"
                    value={shipperState.state}
                    onChange={handleShipperChange}
                    error={shipperErrors.state}
                  />
                </Grid>

                {/* Country & Pincode */}
                <Grid item xs={12} sm={6}>
                  <SBDropdown
                    id="shipper-country-select"
                    label="Country"
                    required
                    name="country"
                    placeholder="Select Country"
                    options={countryList || []}
                    value={shipperState.country ? [shipperState.country] : []}
                    onChange={handleShipperDropdownChange}
                    error={shipperErrors.country}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <SbTextField
                    id="shipper-pincode"
                    label="Pincode / Postal Code"
                    placeholder="Enter pincode"
                    required
                    name="pincode"
                    value={shipperState.pincode}
                    onChange={handleShipperChange}
                    error={shipperErrors.pincode}
                    validationState={validationStates['Shipper.address']}
                    onApplySuggestion={suggestion => {
                      const updated = { ...shipperState, pincode: suggestion };
                      setShipperState(updated);
                      setShipper(updated);
                    }}
                  />
                  {pincodeServiceabilityForShipment !== null && disablePickup === true && (
                    <FormHelperText error sx={{ mt: 0.5 }}>
                      Pickup serviceability not available for this pincode
                    </FormHelperText>
                  )}
                </Grid>

                {/* Alternate Contact Toggle */}
                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      py: 0.5,
                      cursor: 'pointer',
                    }}
                    onClick={() => setShowShipperAltContact(!showShipperAltContact)}
                  >
                    <Typography sx={{ fontSize: '14px', fontWeight: 500, color: 'text.secondary' }}>
                      + Alternate Contact (Optional)
                    </Typography>
                    <IconButton size="small">
                      {showShipperAltContact ? (
                        <ExpandLessIcon fontSize="small" />
                      ) : (
                        <ExpandMoreIcon fontSize="small" />
                      )}
                    </IconButton>
                  </Box>
                  <Collapse in={showShipperAltContact}>
                    <Grid container spacing={2} sx={{ mt: 0.5 }}>
                      <Grid item xs={12} sm={6}>
                        <SbTextField
                          label="Alternate Contact Name"
                          placeholder="Contact person name"
                          name="altContactName"
                          value={shipperState.altContactName || ''}
                          onChange={handleShipperChange}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Contact
                          label={shipperState.altContactName ? 'Alternate Mobile *' : 'Alternate Mobile'}
                          phoneName="altContactNo"
                          phoneNumber={shipperState.altContactNo || ''}
                          onChangePhoneNumber={handleShipperChange}
                          telCountryCodeName="altContactTelephoneCode"
                          telCountryCode={shipperState.altContactTelephoneCode || ''}
                          onChangeTelCountryCode={handleShipperChange}
                          setDefaultTelephone={(val: number) => {
                            const updated = { ...shipperState, altContactTelephoneCode: val.toString() };
                            setShipperState(updated);
                            setShipper(updated);
                          }}
                          error={shipperErrors.altContactNo || shipperErrors.altContactTelephoneCode}
                        />
                      </Grid>
                    </Grid>
                  </Collapse>
                </Grid>

                {/* Save Address As */}
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                    <BookmarkBorderIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                    <TextField
                      placeholder="Save to Address Book as (e.g. Warehouse A, Office)"
                      variant="standard"
                      value={shipperSaveAs}
                      onChange={e => setShipperSaveAs(e.target.value)}
                      fullWidth
                      sx={{ fontSize: '0.875rem' }}
                    />
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* ===================== CONSIGNEE CARD (RECIPIENT) ===================== */}
        <Grid item xs={12} lg={6}>
          <Card
            elevation={0}
            sx={{
              height: '100%',
              borderRadius: '14px',
              border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.04), 0 2px 8px -2px rgba(0, 0, 0, 0.03)',
              display: 'flex',
              flexDirection: 'column',
              bgcolor: theme.palette.background.paper,
            }}
          >
            {/* Consignee Card Header */}
            <Box
              sx={{
                p: { xs: 1.5, sm: 2 },
                borderBottom: `1px solid ${alpha(theme.palette.divider, 0.06)}`,
                bgcolor: alpha(theme.palette.secondary.main || theme.palette.primary.main, 0.02),
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 1.5,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: '10px',
                    bgcolor: alpha(theme.palette.secondary.main || theme.palette.primary.main, 0.1),
                    color: theme.palette.secondary.main || 'primary.main',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <LocationOnOutlinedIcon />
                </Box>
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography sx={{ fontSize: '16px', fontWeight: 500, color: 'primary.main' }}>
                      Consignee Information
                    </Typography>
                    <Chip
                      label="Destination"
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: '0.6875rem',
                        fontWeight: 600,
                        bgcolor: alpha(theme.palette.secondary.main || theme.palette.primary.main, 0.1),
                        color: theme.palette.secondary.main || 'primary.main',
                      }}
                    />
                  </Box>
                  <Typography sx={{ fontSize: '14px', fontWeight: 400, color: 'text.secondary' }}>
                    Recipient details & destination location
                  </Typography>
                </Box>
              </Box>

              <SavedAddresses addresses={consigneeAddresses || []} setSelectedAddress={handleSelectConsigneeAddress} />
            </Box>

            {/* Consignee Form Body */}
            <CardContent sx={{ p: { xs: 1.5, sm: 2 }, flexGrow: 1 }}>
              <Grid container spacing={2}>
                {/* Full Name & Company Name */}
                <Grid item xs={12} sm={6}>
                  <SbTextField
                    id="consignee-name"
                    label="Consignee Full Name"
                    placeholder="Enter recipient name"
                    name="name"
                    required
                    value={consigneeState.name}
                    onChange={handleConsigneeChange}
                    error={consigneeErrors.name}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <SbTextField
                    id="consignee-company"
                    label="Company Name"
                    placeholder="Enter company name"
                    name="companyName"
                    value={consigneeState.companyName}
                    onChange={handleConsigneeChange}
                    error={consigneeErrors.companyName}
                  />
                </Grid>

                {/* Mobile & Email */}
                <Grid item xs={12} sm={6}>
                  <Contact
                    id="consignee-phone"
                    label="Mobile Number *"
                    phoneName="phoneNumber"
                    phoneNumber={consigneeState.phoneNumber || ''}
                    onChangePhoneNumber={handleConsigneeChange}
                    telCountryCodeName="telephoneCode"
                    telCountryCode={consigneeState.telephoneCode || ''}
                    onChangeTelCountryCode={handleConsigneeChange}
                    setDefaultTelephone={(val: number) => {
                      const updated = { ...consigneeState, telephoneCode: val.toString() };
                      setConsigneeState(updated);
                      setConsignee(updated);
                    }}
                    error={consigneeErrors.phoneNumber || consigneeErrors.telephoneCode}
                    validationState={validationStates['Consignee.phoneNumber'] || validationStates['phoneNumber']}
                    onApplySuggestion={suggestion => {
                      const updated = { ...consigneeState, phoneNumber: suggestion };
                      setConsigneeState(updated);
                      setConsignee(updated);
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <SbTextField
                    id="consignee-email"
                    label="Email Address"
                    placeholder="recipient@example.com"
                    name="email"
                    value={consigneeState.email}
                    onChange={handleConsigneeChange}
                    error={consigneeErrors.email}
                    validationState={validationStates['email']}
                    onApplySuggestion={suggestion => {
                      const updated = { ...consigneeState, email: suggestion };
                      setConsigneeState(updated);
                      setConsignee(updated);
                    }}
                  />
                </Grid>

                {/* Google Maps Search */}
                <Grid item xs={12}>
                  <GoogleMaps
                    onSelect={handleConsigneeGoogleAddress}
                    label="Search Address Location"
                    placeholder="Start typing destination locality..."
                    reinitialize={reinitializeConsignee}
                  />
                </Grid>

                {/* Address Line 1 & Line 2 */}
                <Grid item xs={12} sm={6}>
                  <SbTextField
                    id="consignee-line1"
                    label="Address Line 1"
                    placeholder="Flat / Building / Street"
                    required
                    name="line1"
                    value={consigneeState.line1}
                    onChange={handleConsigneeChange}
                    error={consigneeErrors.line1}
                    showCharCount
                    helperText={`${consigneeState.line1 || ''}`}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <SbTextField
                    id="consignee-line2"
                    label="Address Line 2"
                    placeholder="Area / Landmark (Optional)"
                    name="line2"
                    value={consigneeState.line2}
                    onChange={handleConsigneeChange}
                    error={consigneeErrors.line2}
                    showCharCount
                    helperText={`${consigneeState.line2 || ''}`}
                  />
                </Grid>

                {/* City & State */}
                <Grid item xs={12} sm={6}>
                  <SbTextField
                    id="consignee-city"
                    label="City"
                    placeholder="Enter city"
                    required
                    name="city"
                    value={consigneeState.city}
                    onChange={handleConsigneeChange}
                    error={consigneeErrors.city}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <SbTextField
                    id="consignee-state"
                    label="State"
                    placeholder="Enter state"
                    name="state"
                    value={consigneeState.state}
                    onChange={handleConsigneeChange}
                    error={consigneeErrors.state}
                  />
                </Grid>

                {/* Country & Pincode */}
                <Grid item xs={12} sm={6}>
                  <SBDropdown
                    id="consignee-country-select"
                    label="Country"
                    required
                    name="country"
                    placeholder="Select Country"
                    options={countryList || []}
                    value={consigneeState.country ? [consigneeState.country] : []}
                    onChange={handleConsigneeDropdownChange}
                    error={consigneeErrors.country}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <SbTextField
                    id="consignee-pincode"
                    label="Pincode / Postal Code"
                    placeholder="Enter pincode"
                    required
                    name="pincode"
                    value={consigneeState.pincode}
                    onChange={handleConsigneeChange}
                    error={consigneeErrors.pincode}
                    validationState={validationStates['Consignee.address']}
                    onApplySuggestion={suggestion => {
                      const updated = { ...consigneeState, pincode: suggestion };
                      setConsigneeState(updated);
                      setConsignee(updated);
                    }}
                  />
                </Grid>

                {/* Alternate Contact Toggle */}
                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      py: 0.5,
                      cursor: 'pointer',
                    }}
                    onClick={() => setShowConsigneeAltContact(!showConsigneeAltContact)}
                  >
                    <Typography sx={{ fontSize: '14px', fontWeight: 500, color: 'text.secondary' }}>
                      + Alternate Contact (Optional)
                    </Typography>
                    <IconButton size="small">
                      {showConsigneeAltContact ? (
                        <ExpandLessIcon fontSize="small" />
                      ) : (
                        <ExpandMoreIcon fontSize="small" />
                      )}
                    </IconButton>
                  </Box>
                  <Collapse in={showConsigneeAltContact}>
                    <Grid container spacing={2} sx={{ mt: 0.5 }}>
                      <Grid item xs={12} sm={6}>
                        <SbTextField
                          label="Alternate Contact Name"
                          placeholder="Contact person name"
                          name="altContactName"
                          value={consigneeState.altContactName || ''}
                          onChange={handleConsigneeChange}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Contact
                          label={consigneeState.altContactName ? 'Alternate Mobile *' : 'Alternate Mobile'}
                          phoneName="altContactNo"
                          phoneNumber={consigneeState.altContactNo || ''}
                          onChangePhoneNumber={handleConsigneeChange}
                          telCountryCodeName="altContactTelephoneCode"
                          telCountryCode={consigneeState.altContactTelephoneCode || ''}
                          onChangeTelCountryCode={handleConsigneeChange}
                          setDefaultTelephone={(val: number) => {
                            const updated = { ...consigneeState, altContactTelephoneCode: val.toString() };
                            setConsigneeState(updated);
                            setConsignee(updated);
                          }}
                          error={consigneeErrors.altContactNo || consigneeErrors.altContactTelephoneCode}
                        />
                      </Grid>
                    </Grid>
                  </Collapse>
                </Grid>

                {/* Save Address As */}
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                    <BookmarkBorderIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                    <TextField
                      placeholder="Save to Address Book as (e.g. Customer Store, Regional Hub)"
                      variant="standard"
                      value={consigneeSaveAs}
                      onChange={e => setConsigneeSaveAs(e.target.value)}
                      fullWidth
                      sx={{ fontSize: '0.875rem' }}
                    />
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* ===================== DISPATCH METHOD CARD (PICKUP / DROP-OFF) ===================== */}
        {name !== 'Cargo Solutions Network' && (
          <Grid item xs={12}>
            <Card
              elevation={0}
              sx={{
                borderRadius: '14px',
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.04), 0 2px 8px -2px rgba(0, 0, 0, 0.03)',
                bgcolor: theme.palette.background.paper,
                overflow: 'hidden',
              }}
            >
              <Box
                sx={{
                  p: { xs: 1.5, sm: 2 },
                  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.06)}`,
                  bgcolor: alpha(theme.palette.primary.main, 0.02),
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                }}
              >
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: '10px',
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    color: 'primary.main',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <LocalShippingOutlinedIcon />
                </Box>
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography sx={{ fontSize: '16px', fontWeight: 500, color: 'primary.main' }}>
                      Dispatch Method *
                    </Typography>
                    <Chip
                      label="Pickup or Drop-off"
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: '0.6875rem',
                        fontWeight: 600,
                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                        color: 'primary.main',
                      }}
                    />
                  </Box>
                  <Typography sx={{ fontSize: '14px', fontWeight: 400, color: 'text.secondary' }}>
                    Choose whether the carrier collects from the shipper or you drop off at a hub
                  </Typography>
                </Box>
              </Box>

              <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                <Grid container spacing={2}>
                  <ShipmentPickupType
                    pincode={shipperState.pincode}
                    userId={props.userId}
                    warehouse={shipperErrors.warehouse || ''}
                    date={shipperErrors.date || ''}
                    timeSlot={shipperErrors.timeSlot || ''}
                    selectedCountry={shipperState.country}
                  />
                </Grid>
                <Divider sx={{ my: 2 }} />
                <InsurancePackaging />
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>

      {/* Validation Alert Summary Banner */}
      {validationAlert && validationAlert.length > 0 && (
        <Alert
          severity="error"
          onClose={() => setValidationAlert(null)}
          sx={{
            mt: 3,
            borderRadius: '12px',
            fontSize: '0.875rem',
            border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`,
            '& .MuiAlert-message': { width: '100%' },
          }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
            Cannot continue — please complete the following required fields:
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2.5, '& li': { py: 0.25 } }}>
            {validationAlert.map((item, idx) => (
              <li key={idx}>{item}</li>
            ))}
          </Box>
        </Alert>
      )}

      {/* ===================== BOTTOM ACTION BAR ===================== */}
      <Paper
        elevation={0}
        sx={{
          mt: 3,
          p: 2,
          borderRadius: '12px',
          border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.04), 0 2px 10px -2px rgba(0, 0, 0, 0.03)',
          bgcolor: theme.palette.background.paper,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <SbButton
          onClick={handleSaveDraft}
          variant="outlined"
          disabled={!enableSaveAsDraft}
          sx={{
            px: 3,
            py: 1,
            fontWeight: 600,
            borderRadius: '8px',
          }}
        >
          Save as draft
        </SbButton>

        <SbButton
          onClick={handleValidateAndContinue}
          variant="contained"
          endIcon={<ArrowForwardIcon />}
          sx={{
            px: 3.5,
            py: 1.2,
            fontWeight: 700,
            fontSize: '0.925rem',
            borderRadius: '8px',
            boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.25)}`,
          }}
        >
          Continue to Shipment Details
        </SbButton>
      </Paper>
    </Box>
  );
}

export default ShipperConsignee;
